import React, { useState, useEffect } from 'react';
import { useToast } from '../../../components/Toast';
import './EducationProgramHeroForm.css';

const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // Future use for image uploads
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp']; // Future use for image uploads

export default function StudentLifeHouseSystemForm({ apiBase }) {
  const toastUtil = useToast();
  const ToastContainer = toastUtil.ToastContainer;
  const toast = toastUtil;

  // Form values (auto-filled from content_published)
  const [formData, setFormData] = useState({
    kicker: '',
    title: '',
    description: '',
    houses: []
  });

  // Pending approvals per field (kicker, title, description)
  const [pendingFields, setPendingFields] = useState({
    kicker: false,
    title: false,
    description: false
  });

  // Pending approvals per house (house_0, house_1, etc)
  const [pendingHouses, setPendingHouses] = useState({});

  // Track original values to detect changes
  const [originalData, setOriginalData] = useState({...formData});

  const [loading, setLoading] = useState(true);
  const [sectionId, setSectionId] = useState(null);
  const [submitting, setSubmitting] = useState({});
  const [editingHouseIndex, setEditingHouseIndex] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [deletingHouseIndex, setDeletingHouseIndex] = useState(null);
  const [mediaMap, setMediaMap] = useState({});
  const [imageUploading, setImageUploading] = useState({});
  const [imageProgress, setImageProgress] = useState({});

  useEffect(() => {
    const loadData = async () => {
      await Promise.all([loadMediaMap(), loadSection()]);
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  const loadMediaMap = async () => {
    try {
      console.log('[StudentLifeHouseSystemForm] Fetching media map...');
      const response = await fetch(
        `${apiBase}/studentlife/cms/media?_t=${Date.now()}`,
        { 
          credentials: 'include',
          headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
        }
      );
      
      if (!response.ok) {
        console.warn('[StudentLifeHouseSystemForm] Media fetch failed:', response.status);
        return;
      }
      
      const result = await response.json();
      
      if (result.ok && Array.isArray(result.data)) {
        const map = {};
        result.data.forEach((media) => {
          const mediaId = media.id || media.media_id;
          const mediaUrl = media.url || media.file_path || media.filepath || '';
          
          if (mediaId && mediaUrl) {
            map[mediaId] = mediaUrl;
            map[String(mediaId)] = mediaUrl;
            map[parseInt(mediaId)] = mediaUrl;
            console.log(`[StudentLifeHouseSystemForm] ✓ Media ${mediaId} → ${mediaUrl.substring(0, 60)}...`);
          }
        });
        
        console.log('[StudentLifeHouseSystemForm] ✓ Media map ready:', Object.keys(map).length, 'items');
        setMediaMap(map);
      }
    } catch (err) {
      console.error('[StudentLifeHouseSystemForm] Media fetch error:', err.message);
    }
  };

  const loadSection = async () => {
    setLoading(true);
    try {
      console.log('[StudentLifeHouseSystemForm] Loading student life house system section...');
      
      // Approach 1: Try admin endpoint (requires auth)
      console.log('[StudentLifeHouseSystemForm] Trying authenticated endpoint...');
      const adminResponse = await fetch(
        `${apiBase}/studentlife/cms/sections?_t=${Date.now()}`,
        { 
          credentials: 'include',
          headers: { 
            'Content-Type': 'application/json',
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache'
          }
        }
      );
      
      let housesSection = null;
      
      if (adminResponse.ok) {
        const result = await adminResponse.json();
        
        if (result.ok && Array.isArray(result.data)) {
          housesSection = result.data.find(s => s.section_type === 'student-life-house-system');
          console.log('[StudentLifeHouseSystemForm] ✓ Got data from admin endpoint');
        }
      } else if (adminResponse.status === 401) {
        console.log('[StudentLifeHouseSystemForm] Admin endpoint unauthorized, trying public endpoint...');
      }
      
      // Approach 2: Fallback to public endpoint
      if (!housesSection) {
        const publicResponse = await fetch(
          `${apiBase}/studentlife/cms/sections?published=true&_t=${Date.now()}`,
          { 
            credentials: 'include',
            headers: { 
              'Content-Type': 'application/json',
              'Cache-Control': 'no-cache, no-store, must-revalidate',
              'Pragma': 'no-cache'
            }
          }
        );
        
        if (publicResponse.ok) {
          const result = await publicResponse.json();
          
          if (result.ok && Array.isArray(result.data)) {
            housesSection = result.data.find(s => s.section_type === 'student-life-house-system');
            console.log('[StudentLifeHouseSystemForm] ✓ Got data from public endpoint');
          }
        } else {
          throw new Error(`Public endpoint returned ${publicResponse.status}`);
        }
      }
      
      if (!housesSection) {
        throw new Error('House System section not found');
      }

      const sectionId = housesSection.id;
      setSectionId(sectionId);

      console.log('[StudentLifeHouseSystemForm] Found house system section:', sectionId);

      // Load section with pending changes (if authenticated)
      let pendingFieldsMap = { kicker: false, title: false, description: false };
      let pendingHousesMap = {};
      
      const pendingResponse = await fetch(
        `${apiBase}/cms/section/${sectionId}/with-pending`,
        { 
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        }
      );
      
      if (pendingResponse.ok) {
        try {
          const pendingResult = await pendingResponse.json();
          
          if (pendingResult.success && pendingResult.data && pendingResult.data.pending_changes) {
            const { pending_changes } = pendingResult.data;

            if (Array.isArray(pending_changes)) {
              pending_changes.forEach(change => {
                if (change.status === 'pending' && change.locale === 'en') {
                  if (change.field_id === 'kicker' || change.field_id === 'title' || change.field_id === 'description') {
                    pendingFieldsMap[change.field_id] = true;
                  } else if (change.field_id && change.field_id.includes('houses.')) {
                    const match = change.field_id.match(/houses\.(\d+)\./);
                    if (match) {
                      const houseIdx = parseInt(match[1]);
                      pendingHousesMap[`house_${houseIdx}`] = true;
                    }
                  }
                }
              });
            }
          }
        } catch (e) {
          console.warn('[StudentLifeHouseSystemForm] Error parsing pending changes:', e);
        }
      }

      // Extract published content
      if (housesSection.content_published) {
        let published = housesSection.content_published;
        
        if (typeof published === 'string') {
          try {
            published = JSON.parse(published);
          } catch (e) {
            published = {};
          }
        }

        const publishedEn = published.en || {};
        
        const initialData = {
          kicker: publishedEn.kicker || '',
          title: publishedEn.title || '',
          description: publishedEn.description || '',
          houses: Array.isArray(publishedEn.houses) ? publishedEn.houses : []
        };

        console.log(`[StudentLifeHouseSystemForm] Initial data loaded:`, {
          kicker: initialData.kicker,
          title: initialData.title,
          description: initialData.description?.substring(0, 80),
          housesCount: initialData.houses.length
        });

        setFormData(initialData);
        setOriginalData(initialData);
        setPendingFields(pendingFieldsMap);
        setPendingHouses(pendingHousesMap);

        console.log('[StudentLifeHouseSystemForm] ✓ Section loaded:', {
          id: sectionId,
          housesCount: initialData.houses.length,
          pendingFields: pendingFieldsMap,
          pendingHouses: pendingHousesMap
        });
      }
    } catch (err) {
      console.error('[StudentLifeHouseSystemForm] Load error:', err);
      toast.error('Error loading form: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const handleHouseChange = (index, field, value) => {
    setFormData(prev => {
      const newHouses = [...prev.houses];
      if (typeof newHouses[index] !== 'object' || newHouses[index] === null) {
        newHouses[index] = {};
      }
      newHouses[index] = {
        ...newHouses[index],
        [field]: value
      };
      return {
        ...prev,
        houses: newHouses
      };
    });
  };

  const handleImageUpload = async (houseIndex, imgIndex, e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_IMAGE_SIZE) {
      toast.error(`Max 5MB, got ${(file.size / 1024 / 1024).toFixed(2)}MB`);
      return;
    }

    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error('Only JPG, PNG, WebP allowed');
      return;
    }

    const key = `${houseIndex}-${imgIndex}`;
    setImageUploading(prev => ({ ...prev, [key]: true }));
    setImageProgress(prev => ({ ...prev, [key]: 0 }));

    try {
      const formDataToUpload = new FormData();
      formDataToUpload.append('file', file);

      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percent = Math.round((e.loaded / e.total) * 100);
          console.log('[StudentLifeHouseSystemForm] Upload progress:', percent + '%');
          setImageProgress(prev => ({ ...prev, [key]: percent }));
        }
      });

      xhr.addEventListener('load', () => {
        console.log('[StudentLifeHouseSystemForm] Upload completed, status:', xhr.status);
        console.log('[StudentLifeHouseSystemForm] Response text:', xhr.responseText);
        
        if (xhr.status === 200 || xhr.status === 201) {
          try {
            const response = JSON.parse(xhr.responseText);
            console.log('[StudentLifeHouseSystemForm] Parsed response:', response);
            
            if (response.success || response.ok) {
              const mediaId = response.media_id || response.id || response.filename;
              console.log('[StudentLifeHouseSystemForm] ✓ Image uploaded, media ID:', mediaId);
              
              if (mediaId) {
                // Update single image field (not images array)
                handleHouseChange(houseIndex, 'image', mediaId);
                
                // Update mediaMap locally so preview shows immediately
                setMediaMap(prev => ({
                  ...prev,
                  [mediaId]: response.url || response.file_path || `/api/uploads/studentlife/${response.filename}`
                }));
                
                console.log('[StudentLifeHouseSystemForm] Image field updated to:', mediaId);
                toast.success('✓ Image uploaded (ID: ' + mediaId + ')');
              } else {
                toast.error('No media ID in response');
              }
            } else {
              toast.error(response.error || 'Upload failed');
            }
          } catch (parseErr) {
            console.error('[StudentLifeHouseSystemForm] JSON parse error:', parseErr);
            toast.error('Invalid server response');
          }
        } else {
          console.error('[StudentLifeHouseSystemForm] Upload failed with status', xhr.status);
          toast.error('Upload failed (HTTP ' + xhr.status + ')');
        }
        setImageUploading(prev => ({ ...prev, [key]: false }));
      });

      xhr.addEventListener('error', () => {
        console.error('[StudentLifeHouseSystemForm] XHR error event');
        toast.error('Upload failed - network error');
        setImageUploading(prev => ({ ...prev, [key]: false }));
      });

      console.log('[StudentLifeHouseSystemForm] Opening XHR to:', `${apiBase}/studentlife/cms/media/upload`);
      xhr.open('POST', `${apiBase}/studentlife/cms/media/upload`);
      xhr.withCredentials = true;
      xhr.send(formDataToUpload);
    } catch (err) {
      console.error('[StudentLifeHouseSystemForm] Upload error:', err);
      toast.error('Upload error: ' + err.message);
      setImageUploading(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleSubmitField = async (fieldName) => {
    if (!sectionId) {
      toast.error('Section ID not loaded');
      return;
    }

    // Check if value changed
    if (JSON.stringify(formData[fieldName]) === JSON.stringify(originalData[fieldName])) {
      toast.warning('No changes to submit');
      return;
    }

    console.log(`[StudentLifeHouseSystemForm] Submitting field: ${fieldName}`, {
      old: originalData[fieldName],
      new: formData[fieldName],
      sectionId
    });

    setSubmitting(prev => ({ ...prev, [fieldName]: true }));

    try {
      const requestBody = {
        section_id: sectionId,
        field_id: fieldName,
        field_name: fieldName.replace(/_/g, ' '),
        new_value: formData[fieldName],
        old_value: originalData[fieldName],
        locale: 'en'
      };
      
      console.log(`[StudentLifeHouseSystemForm] Sending request:`, JSON.stringify(requestBody));

      const response = await fetch(
        `${apiBase}/cms/field-change/submit`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(requestBody)
        }
      );

      const result = await response.json();
      console.log(`[StudentLifeHouseSystemForm] Response from submit:`, result);

      if (result.success) {
        toast.success(`Submitted for approval`);
        
        // Mark as pending locally
        setPendingFields(prev => ({
          ...prev,
          [fieldName]: true
        }));

        // Update original data
        setOriginalData(prev => ({
          ...prev,
          [fieldName]: formData[fieldName]
        }));
      } else {
        if (response.status === 409) {
          toast.error('This field already has a pending approval');
        } else {
          toast.error(result.error || result.message || 'Submit failed');
        }
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [fieldName]: false }));
    }
  };

  const handleSubmitHouse = async (houseIndex, operation = null) => {
    if (!sectionId) {
      toast.error('Section ID not loaded');
      return;
    }

    const house = formData.houses[houseIndex];
    if (!house) {
      toast.error('House not found');
      return;
    }

    // Validate house has name (not needed for delete)
    if (operation !== 'delete' && (!house.name || house.name.trim() === '')) {
      toast.error('House name is required');
      return;
    }

    // Auto-detect operation if not provided
    if (!operation) {
      // isNewHouse: house exists in formData but NOT in originalData
      const isNewHouse = !originalData.houses[houseIndex];
      operation = isNewHouse ? 'add' : 'update';
    }

    const houseKey = `house_${houseIndex}`;
    setSubmitting(prev => ({ ...prev, [houseKey]: true }));

    try {
      // Helper function to filter out empty strings and whitespace-only entries
      const filterEmptyStrings = (arr) => {
        if (!Array.isArray(arr)) return [];
        return arr.filter(item => {
          if (typeof item === 'string') {
            return item.trim() !== '';
          }
          return item != null; // Keep non-null objects/numbers
        });
      };

      const houseToSubmit = operation === 'delete' ? null : {
        ...house,
        values: filterEmptyStrings(house.values),
        activities: filterEmptyStrings(house.activities),
        achievements: filterEmptyStrings(house.achievements)
      };

      const fieldId = `houses.${houseIndex}.${operation}`;
      const fieldName = operation === 'add' ? `Add House: ${house.name}` :
                        operation === 'update' ? `Update House: ${house.name}` :
                        `Delete House: ${house.name}`;

      console.log('[StudentLifeHouseSystemForm] Submitting house operation:', {
        houseIndex, operation, fieldId,
        isNew: !originalData.houses[houseIndex],
        sectionId
      });

      const response = await fetch(
        `${apiBase}/cms/field-change/submit`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            section_id: sectionId,
            field_id: fieldId,
            field_name: fieldName,
            old_value: originalData.houses[houseIndex] || null,
            new_value: houseToSubmit,
            locale: 'en',
            operation_type: operation
          })
        }
      );

      const result = await response.json();
      console.log('[StudentLifeHouseSystemForm] Submit response:', result);

      if (result.success) {
        const opLabel = operation === 'add' ? 'added' : operation === 'update' ? 'updated' : 'deleted';
        toast.success(`House ${opLabel} and submitted for approval`);

        if (operation === 'add') {
          // Mark pending for this new house index
          setPendingHouses(prev => ({ ...prev, [houseKey]: true }));
          // Update originalData to include this new house so subsequent edits use 'update'
          setOriginalData(prev => ({
            ...prev,
            houses: [...prev.houses, houseToSubmit]
          }));
        } else if (operation === 'update') {
          setPendingHouses(prev => ({ ...prev, [houseKey]: true }));
          setOriginalData(prev => ({
            ...prev,
            houses: prev.houses.map((h, i) => i === houseIndex ? houseToSubmit : h)
          }));
        } else if (operation === 'delete') {
          // Remove from both formData and originalData locally
          setFormData(prev => ({
            ...prev,
            houses: prev.houses.filter((_, i) => i !== houseIndex)
          }));
          setOriginalData(prev => ({
            ...prev,
            houses: prev.houses.filter((_, i) => i !== houseIndex)
          }));
          setPendingHouses(prev => {
            const next = { ...prev };
            delete next[houseKey];
            return next;
          });
        }

        setEditingHouseIndex(null);
        setDeletingHouseIndex(null);
      } else {
        if (response.status === 409) {
          toast.error(`House already has a pending approval`);
        } else {
          toast.error(result.error || result.message || 'Submit failed');
        }
      }
    } catch (err) {
      console.error('[StudentLifeHouseSystemForm] Submit error:', err);
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [houseKey]: false }));
    }
  };

  if (loading) {
    return (
      <div className="education-hero-form-container">
        <div className="loading">
          <div className="loading-spinner"></div>
          Loading House System Section...
        </div>
      </div>
    );
  }

  return (
    <div className="education-hero-form-container">
      <ToastContainer />

      <div className="hero-form-wrapper">
        <div className="hero-form-header">
          <h2 className="hero-form-title">House System</h2>
          <p className="hero-form-description">Manage house system section header, content, and individual house details</p>
        </div>

        {/* KICKER SECTION */}
        <div className="form-section">
          <h3 className="form-section-title">Section Kicker</h3>
          <div className="form-field-card">
            <div className="field-header">
              <label>Kicker *</label>
              {pendingFields.kicker && (
                <span className="status-badge pending">⏳ Pending</span>
              )}
            </div>
            <input
              type="text"
              maxLength="100"
              value={formData.kicker}
              onChange={(e) => handleFieldChange('kicker', e.target.value)}
              disabled={submitting.kicker || pendingFields.kicker}
              placeholder="e.g., House System"
              className="text-input"
            />
            <button
              onClick={() => handleSubmitField('kicker')}
              disabled={submitting.kicker || pendingFields.kicker || formData.kicker === originalData.kicker}
              className="submit-btn"
            >
              {submitting.kicker ? 'Submitting...' : 'Submit for Approval'}
            </button>
          </div>
        </div>

        {/* TITLE SECTION */}
        <div className="form-section">
          <h3 className="form-section-title">Section Title</h3>
          <div className="form-field-card">
            <div className="field-header">
              <label>Title *</label>
              {pendingFields.title && (
                <span className="status-badge pending">⏳ Pending</span>
              )}
            </div>
            <input
              type="text"
              maxLength="200"
              value={formData.title}
              onChange={(e) => handleFieldChange('title', e.target.value)}
              disabled={submitting.title || pendingFields.title}
              placeholder="e.g., Discover Your House"
              className="text-input"
            />
            <button
              onClick={() => handleSubmitField('title')}
              disabled={submitting.title || pendingFields.title || formData.title === originalData.title}
              className="submit-btn"
            >
              {submitting.title ? 'Submitting...' : 'Submit for Approval'}
            </button>
          </div>
        </div>

        {/* DESCRIPTION SECTION */}
        <div className="form-section">
          <h3 className="form-section-title">Section Description</h3>
          <div className="form-field-card">
            <div className="field-header">
              <label>Description *</label>
              {pendingFields.description && (
                <span className="status-badge pending">⏳ Pending</span>
              )}
            </div>
            <textarea
              maxLength="500"
              value={formData.description}
              onChange={(e) => handleFieldChange('description', e.target.value)}
              disabled={submitting.description || pendingFields.description}
              placeholder="Brief description of the house system..."
              className="textarea-input"
              rows="4"
            />
            <button
              onClick={() => handleSubmitField('description')}
              disabled={submitting.description || pendingFields.description || formData.description === originalData.description}
              className="submit-btn"
            >
              {submitting.description ? 'Submitting...' : 'Submit for Approval'}
            </button>
          </div>
        </div>

        {/* HOUSES SECTION */}
        <div className="form-section">
          <h3 className="form-section-title">Houses ({formData.houses.length})</h3>

          <div className="cards-grid">
            {formData.houses.length === 0 ? (
              <div className="items-empty-state" style={{ gridColumn: '1 / -1' }}>
                <div className="empty-icon">🏠</div>
                <h4>No houses configured</h4>
                <p>Click "Add New House" to create your first house</p>
              </div>
            ) : (
              formData.houses.map((house, idx) => {
                // Use single image field (not images array)
                const houseImage = house?.image || '';
                
                return (
                  <div
                    key={idx}
                    className="slide-card"
                    onClick={() => setEditingHouseIndex(idx)}
                    style={{ cursor: 'pointer', display: 'flex', flexDirection: 'column' }}
                  >
                    {/* Image or Color Header */}
                    {houseImage && mediaMap[houseImage] ? (
                      <div className="card-image">
                        <img src={mediaMap[houseImage]} alt={house.name || `House ${idx + 1}`} style={{ objectFit: 'cover' }} />
                      </div>
                    ) : (
                      <div 
                        style={{ 
                          backgroundColor: house.color || '#4299e1',
                          color: 'white',
                          padding: '20px',
                          textAlign: 'center',
                          borderBottom: '1px solid rgba(255,255,255,0.1)',
                          minHeight: '160px',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          alignItems: 'center'
                        }}
                      >
                        <div style={{ fontSize: '48px', marginBottom: '8px' }}>
                          {house.emoji || '🏛️'}
                        </div>
                        <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: '700' }}>
                          {house.name || 'House'}
                        </h4>
                      </div>
                    )}

                    {/* Card Content */}
                    <div className="card-content" style={{ padding: '16px', flex: 1 }}>
                      <div className="card-order">House {idx + 1}</div>
                      <h4 className="card-title" style={{ marginBottom: '4px' }}>{house.name || 'House'}</h4>
                      <p style={{ fontSize: '12px', margin: '0 0 8px 0', fontStyle: 'italic', color: '#718096' }}>
                        "{house.motto || 'House Motto'}"
                      </p>
                      
                      {/* Quick Info */}
                      <div style={{ fontSize: '12px', color: '#718096', lineHeight: '1.5' }}>
                        {house.houseLeader && (
                          <div>👤 {house.houseLeader}</div>
                        )}
                        {house.members && (
                          <div>👥 {house.members}</div>
                        )}
                        {Array.isArray(house.values) && house.values.length > 0 && (
                          <div>⭐ {house.values.length} Values</div>
                        )}
                      </div>
                    </div>

                    {/* Pending Badge */}
                    {pendingHouses[`house_${idx}`] && (
                      <div className="card-pending">⏳</div>
                    )}
                  </div>
                );
              })
            )}

            {/* Add New House Card */}
            <div
              className="slide-card add-card"
              onClick={() => {
                const newHouse = {
                  name: '',
                  motto: '',
                  description: '',
                  color: '#4299e1',
                  icon: 'fa-shield',
                  houseLeader: '',
                  members: '',
                  values: [],
                  activities: [],
                  achievements: []
                };
                setFormData(prev => {
                  const updated = {
                    ...prev,
                    houses: [...prev.houses, newHouse]
                  };
                  // Set editing to new house after state updates
                  setTimeout(() => setEditingHouseIndex(updated.houses.length - 1), 0);
                  return updated;
                });
              }}
              style={{ cursor: 'pointer', opacity: 0.7 }}
            >
              <div className="card-content" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '48px', marginBottom: '12px' }}>➕</div>
                  <h4 style={{ margin: 0, fontSize: '16px' }}>Add New House</h4>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* EDIT HOUSE MODAL */}
        {editingHouseIndex !== null && formData.houses[editingHouseIndex] && (
          <div className="modal-overlay" onClick={() => setEditingHouseIndex(null)}>
            <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Edit House: {formData.houses[editingHouseIndex].name || `House ${editingHouseIndex + 1}`}</h3>
                <button className="modal-close" onClick={() => setEditingHouseIndex(null)}>×</button>
              </div>

              <div className="modal-body">
                <div className="modal-field">
                  <label>House Name</label>
                  <input
                    type="text"
                    value={formData.houses[editingHouseIndex].name || ''}
                    onChange={(e) => handleHouseChange(editingHouseIndex, 'name', e.target.value)}
                    placeholder="e.g., Phoenix House"
                    className="text-input"
                  />
                </div>

                <div className="modal-field">
                  <label>House Motto</label>
                  <input
                    type="text"
                    value={formData.houses[editingHouseIndex].motto || ''}
                    onChange={(e) => handleHouseChange(editingHouseIndex, 'motto', e.target.value)}
                    placeholder="e.g., Rise and Shine"
                    className="text-input"
                  />
                </div>

                <div className="modal-field">
                  <label>House Color</label>
                  <input
                    type="color"
                    value={formData.houses[editingHouseIndex].color || '#4299e1'}
                    onChange={(e) => handleHouseChange(editingHouseIndex, 'color', e.target.value)}
                    className="text-input"
                    style={{ height: '40px', padding: '4px', cursor: 'pointer' }}
                  />
                </div>

                <div className="modal-field">
                  <label>Description</label>
                  <textarea
                    value={formData.houses[editingHouseIndex].description || ''}
                    onChange={(e) => handleHouseChange(editingHouseIndex, 'description', e.target.value)}
                    placeholder="Detailed description of the house..."
                    className="textarea-input"
                    rows="3"
                  />
                </div>

                <div className="modal-field">
                  <label>House Leader</label>
                  <input
                    type="text"
                    value={formData.houses[editingHouseIndex].houseLeader || ''}
                    onChange={(e) => handleHouseChange(editingHouseIndex, 'houseLeader', e.target.value)}
                    placeholder="e.g., John Smith"
                    className="text-input"
                  />
                </div>

                <div className="modal-field">
                  <label>Members Info</label>
                  <input
                    type="text"
                    value={formData.houses[editingHouseIndex].members || ''}
                    onChange={(e) => handleHouseChange(editingHouseIndex, 'members', e.target.value)}
                    placeholder="e.g., Grades 7-12"
                    className="text-input"
                  />
                </div>

                <div className="modal-field">
                  <label>Highlights</label>
                  <textarea
                    value={formData.houses[editingHouseIndex].highlights || ''}
                    onChange={(e) => handleHouseChange(editingHouseIndex, 'highlights', e.target.value)}
                    placeholder="Key highlights..."
                    className="textarea-input"
                    rows="2"
                  />
                </div>

                <div className="modal-field">
                  <label>House Icon</label>
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '12px' }}>
                    <select
                      value={formData.houses[editingHouseIndex].icon || 'fa-shield'}
                      onChange={(e) => handleHouseChange(editingHouseIndex, 'icon', e.target.value)}
                      className="text-input"
                      style={{ flex: 1 }}
                    >
                      <option value="fa-shield">🛡️ Shield (Default)</option>
                      <option value="fa-heart">❤️ Heart</option>
                      <option value="fa-star">⭐ Star</option>
                      <option value="fa-bolt">⚡ Lightning Bolt</option>
                      <option value="fa-fire">🔥 Fire</option>
                      <option value="fa-crown">👑 Crown</option>
                      <option value="fa-trophy">🏆 Trophy</option>
                      <option value="fa-flag">🚩 Flag</option>
                      <option value="fa-gem">💎 Gem</option>
                      <option value="fa-book">📖 Book</option>
                      <option value="fa-graduation-cap">🎓 Graduation Cap</option>
                      <option value="fa-lightbulb">💡 Light Bulb</option>
                      <option value="fa-rocket">🚀 Rocket</option>
                      <option value="fa-compass">🧭 Compass</option>
                      <option value="fa-mountain">⛰️ Mountain</option>
                      <option value="fa-tree">🌲 Tree</option>
                      <option value="fa-sun">☀️ Sun</option>
                      <option value="fa-moon">🌙 Moon</option>
                      <option value="fa-cloud">☁️ Cloud</option>
                      <option value="fa-dove">🕊️ Dove</option>
                      <option value="fa-cross">✝️ Cross</option>
                      <option value="fa-hands-praying">🙏 Praying Hands</option>
                      <option value="fa-anchor">⚓ Anchor</option>
                      <option value="fa-feather">🪶 Feather</option>
                    </select>
                    <div style={{ 
                      width: '60px', 
                      height: '60px', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      backgroundColor: formData.houses[editingHouseIndex].color || '#4299e1',
                      color: 'white',
                      borderRadius: '8px',
                      fontSize: '28px'
                    }}>
                      <i className={`fas ${formData.houses[editingHouseIndex].icon || 'fa-shield'}`} />
                    </div>
                  </div>
                  <div style={{ fontSize: '12px', color: '#718096', marginTop: '8px' }}>
                    💡 Select an icon that represents your house's values and identity.
                  </div>
                </div>

                {/* HOUSE IMAGE */}
                <div className="modal-field">
                  <label>House Image</label>
                  <div style={{ marginBottom: '12px' }}>
                    {(() => {
                      const img = formData.houses[editingHouseIndex]?.image;
                      const imgUrl = img ? mediaMap[img] : null;
                      return (
                        <div style={{ border: '1px dashed #ccc', borderRadius: '8px', overflow: 'hidden', maxWidth: '400px' }}>
                          {imgUrl && (
                            <img
                              src={imgUrl}
                              alt={`House ${editingHouseIndex + 1} Image`}
                              style={{ width: '100%', height: '200px', objectFit: 'cover' }}
                            />
                          )}
                          <div style={{ padding: '12px', background: '#f9fafb' }}>
                            <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
                              {img ? `Image ID: ${img}` : 'No image uploaded'}
                            </div>
                            <label style={{ display: 'block', cursor: 'pointer', padding: '8px', background: '#e0f2fe', border: '1px solid #0284c7', borderRadius: '4px', textAlign: 'center', fontSize: '12px', fontWeight: '500' }}>
                              {imageUploading[`${editingHouseIndex}-0`] 
                                ? `Uploading ${imageProgress[`${editingHouseIndex}-0`] || 0}%`
                                : 'Upload Image'
                              }
                              <input
                                type="file"
                                onChange={(e) => handleImageUpload(editingHouseIndex, 0, e)}
                                disabled={imageUploading[`${editingHouseIndex}-0`]}
                                accept="image/*"
                                style={{ display: 'none' }}
                              />
                            </label>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>

                {/* HIGHLIGHTS */}
                <div className="modal-field">
                  <label>Core Values</label>
                  <div style={{ marginBottom: '12px' }}>
                    {(() => {
                      const values = formData.houses[editingHouseIndex]?.values;
                      const valuesArray = Array.isArray(values) ? [...values] : [];
                      // Ensure at least 3 value slots
                      while (valuesArray.length < 3) {
                        valuesArray.push('');
                      }
                      return valuesArray.map((value, vIdx) => (
                        <div key={vIdx} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                          <input
                            type="text"
                            value={value || ''}
                            onChange={(e) => {
                              const updated = [...valuesArray];
                              updated[vIdx] = e.target.value;
                              handleHouseChange(editingHouseIndex, 'values', updated.filter(Boolean));
                            }}
                            placeholder={`Value ${vIdx + 1}`}
                            className="text-input"
                            style={{ flex: 1 }}
                          />
                          {vIdx >= 3 && (
                            <button
                              onClick={() => {
                                const updated = valuesArray.filter((_, i) => i !== vIdx);
                                handleHouseChange(editingHouseIndex, 'values', updated.filter(Boolean));
                              }}
                              style={{ padding: '8px 12px', background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: '4px', cursor: 'pointer' }}
                            >
                              ✕
                            </button>
                          )}
                        </div>
                      ));
                    })()}
                    <button
                      onClick={() => {
                        const updated = [...(formData.houses[editingHouseIndex]?.values || [])];
                        updated.push('');
                        handleHouseChange(editingHouseIndex, 'values', updated);
                      }}
                      style={{ marginTop: '8px', padding: '8px 12px', background: '#e0f2fe', border: '1px solid #0284c7', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' }}
                    >
                      + Add Value
                    </button>
                  </div>
                </div>

                {/* ACTIVITIES */}
                <div className="modal-field">
                  <label>Activities</label>
                  <div style={{ marginBottom: '12px' }}>
                    {(() => {
                      const activities = formData.houses[editingHouseIndex]?.activities;
                      const activitiesArray = Array.isArray(activities) ? [...activities] : [];
                      // Ensure at least 3 activity slots
                      while (activitiesArray.length < 3) {
                        activitiesArray.push('');
                      }
                      return activitiesArray.map((activity, aIdx) => (
                        <div key={aIdx} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                          <input
                            type="text"
                            value={activity || ''}
                            onChange={(e) => {
                              const updated = [...activitiesArray];
                              updated[aIdx] = e.target.value;
                              handleHouseChange(editingHouseIndex, 'activities', updated.filter(Boolean));
                            }}
                            placeholder={`Activity ${aIdx + 1}`}
                            className="text-input"
                            style={{ flex: 1 }}
                          />
                          {aIdx >= 3 && (
                            <button
                              onClick={() => {
                                const updated = activitiesArray.filter((_, i) => i !== aIdx);
                                handleHouseChange(editingHouseIndex, 'activities', updated.filter(Boolean));
                              }}
                              style={{ padding: '8px 12px', background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: '4px', cursor: 'pointer' }}
                            >
                              ✕
                            </button>
                          )}
                        </div>
                      ));
                    })()}
                    <button
                      onClick={() => {
                        const updated = [...(formData.houses[editingHouseIndex]?.activities || [])];
                        updated.push('');
                        handleHouseChange(editingHouseIndex, 'activities', updated);
                      }}
                      style={{ marginTop: '8px', padding: '8px 12px', background: '#e0f2fe', border: '1px solid #0284c7', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' }}
                    >
                      + Add Activity
                    </button>
                  </div>
                </div>

                {/* ACHIEVEMENTS */}
                <div className="modal-field">
                  <label>Achievements</label>
                  <div style={{ marginBottom: '12px' }}>
                    {(() => {
                      const achievements = formData.houses[editingHouseIndex]?.achievements;
                      const achievementsArray = Array.isArray(achievements) ? [...achievements] : [];
                      // Ensure at least 3 achievement slots
                      while (achievementsArray.length < 3) {
                        achievementsArray.push('');
                      }
                      return achievementsArray.map((achievement, acIdx) => (
                        <div key={acIdx} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                          <input
                            type="text"
                            value={achievement || ''}
                            onChange={(e) => {
                              const updated = [...achievementsArray];
                              updated[acIdx] = e.target.value;
                              handleHouseChange(editingHouseIndex, 'achievements', updated.filter(Boolean));
                            }}
                            placeholder={`Achievement ${acIdx + 1}`}
                            className="text-input"
                            style={{ flex: 1 }}
                          />
                          {acIdx >= 3 && (
                            <button
                              onClick={() => {
                                const updated = achievementsArray.filter((_, i) => i !== acIdx);
                                handleHouseChange(editingHouseIndex, 'achievements', updated.filter(Boolean));
                              }}
                              style={{ padding: '8px 12px', background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: '4px', cursor: 'pointer' }}
                            >
                              ✕
                            </button>
                          )}
                        </div>
                      ));
                    })()}
                    <button
                      onClick={() => {
                        const updated = [...(formData.houses[editingHouseIndex]?.achievements || [])];
                        updated.push('');
                        handleHouseChange(editingHouseIndex, 'achievements', updated);
                      }}
                      style={{ marginTop: '8px', padding: '8px 12px', background: '#e0f2fe', border: '1px solid #0284c7', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' }}
                    >
                      + Add Achievement
                    </button>
                  </div>
                </div>

                {/* ACTION BUTTONS */}
                <div style={{ display: 'flex', gap: '12px', marginTop: '24px', paddingTop: '12px', borderTop: '1px solid #e5e7eb' }}>
                  <button
                    onClick={() => handleSubmitHouse(editingHouseIndex)}
                    disabled={submitting[`house_${editingHouseIndex}`]}
                    style={{
                      flex: 1,
                      padding: '10px 16px',
                      background: '#16a34a',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: '500'
                    }}
                  >
                    {submitting[`house_${editingHouseIndex}`] ? 'Submitting...' : 'Submit for Approval'}
                  </button>
                  {originalData.houses[editingHouseIndex] && (
                    <button
                      onClick={() => {
                        if (window.confirm('Delete this house?')) {
                          handleSubmitHouse(editingHouseIndex, 'delete');
                        }
                      }}
                      disabled={submitting[`house_${editingHouseIndex}`]}
                      style={{
                        padding: '10px 16px',
                        background: '#dc2626',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontWeight: '500'
                      }}
                    >
                      Delete
                    </button>
                  )}
                  <button
                    onClick={() => setEditingHouseIndex(null)}
                    style={{
                      padding: '10px 16px',
                      background: '#e5e7eb',
                      color: '#374151',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: '500'
                    }}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
